import api from '../api';
import {
    applyJobLiveNotification,
    isPrinterLivePrinting,
} from './jobLiveInProgress';

export const JOB_ISSUE_STATUSES = ['falha', 'cancelado', 'parcial', 'fator_externo'];

export function isJobIssueStatus(status) {
    return JOB_ISSUE_STATUSES.includes(status);
}

export function patchJobsSummary(summary, detail) {
    if (!summary || !detail) {
        return summary;
    }

    const next = { ...summary };

    if (detail.action === 'started') {
        next.in_progress = (next.in_progress || 0) + 1;
        next.total = (next.total || 0) + 1;
        next.total_objects = (next.total_objects || 0) + 1;
        return next;
    }

    if (detail.action === 'finished') {
        next.in_progress = Math.max(0, (next.in_progress || 0) - 1);
        if (detail.status === 'sucesso') {
            next.completed = (next.completed || 0) + 1;
        } else if (isJobIssueStatus(detail.status)) {
            next.issues = (next.issues || 0) + 1;
        }
    }

    return next;
}

export function patchOverviewCardsForNotification(cards, detail) {
    if (!Array.isArray(cards) || !detail?.printerUuid) {
        return cards;
    }

    applyJobLiveNotification(detail);

    return cards.map((card) => {
        if (card.printer_uuid !== detail.printerUuid) {
            return card;
        }

        const next = {
            ...card,
            is_printing: isPrinterLivePrinting(detail.printerUuid),
        };

        if (detail.action === 'started') {
            next.total_jobs = (next.total_jobs || 0) + 1;
        }

        return next;
    });
}

export function patchPrinterStatsForNotification(stats, detail) {
    if (!Array.isArray(stats) || !detail?.printerUuid) {
        return stats;
    }

    return stats.map((stat) => {
        if (stat.printer_uuid !== detail.printerUuid) {
            return stat;
        }

        if (detail.action === 'started') {
            return {
                ...stat,
                total_jobs: (stat.total_jobs || 0) + 1,
            };
        }

        return stat;
    });
}

export function patchPrintersInProgressSet(prevSet, detail) {
    const next = new Set(prevSet);
    applyJobLiveNotification(detail);

    if (isPrinterLivePrinting(detail.printerUuid)) {
        next.add(detail.printerUuid);
    } else {
        next.delete(detail.printerUuid);
    }

    return next;
}

export function jobMatchesListFilters(job, filters = {}) {
    if (!job) {
        return false;
    }

    if (filters.printer_uuid && job.printer_uuid !== filters.printer_uuid) {
        return false;
    }

    if (filters.status && job.status !== filters.status) {
        return false;
    }

    const search = String(filters.searchTerm || '').trim().toLowerCase();
    if (filters.appliedSearchTerm && search) {
        const haystack = [
            job.file_name,
            job.job_id,
            job.status,
            job.printer_uuid,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        if (!haystack.includes(search)) {
            return false;
        }
    }

    return true;
}

export function mergeJobInList(jobs, job, { filters, currentPage, pageSize }) {
    if (!Array.isArray(jobs) || !job) {
        return jobs;
    }

    const jobId = job.id || job.uuid;
    if (!jobId) {
        return jobs;
    }

    const matches = jobMatchesListFilters(job, filters);
    const existingIndex = jobs.findIndex((row) => (row.id || row.uuid) === jobId);

    if (!matches) {
        if (existingIndex === -1) {
            return jobs;
        }
        return jobs.filter((_, index) => index !== existingIndex);
    }

    if (existingIndex >= 0) {
        const next = [...jobs];
        next[existingIndex] = { ...next[existingIndex], ...job, id: jobId, uuid: job.uuid || jobId };
        return next;
    }

    if (currentPage !== 1) {
        return jobs;
    }

    const next = [{ ...job, id: jobId, uuid: job.uuid || jobId }, ...jobs];
    return next.slice(0, pageSize || next.length);
}

export async function fetchJobDetails(jobUuid) {
    if (!jobUuid) {
        return null;
    }

    try {
        const response = await api.get(`jobs/${jobUuid}`);
        const data = response.data?.data;
        if (!data || response.data?.success === false) {
            return null;
        }
        return {
            ...data,
            id: data.id || data.uuid || jobUuid,
            uuid: data.uuid || data.id || jobUuid,
        };
    } catch {
        return null;
    }
}
